import clsx from 'clsx';

/**
 * Label + input wrapper. Mirrors the desktop's `Input.jsx` visual recipe
 * (frontend/src/components/Input.jsx) — same height, same radius, same focus
 * pattern — so the two apps feel like one product. The only deliberate
 * mobile-specific divergence is `text-body` (16px) on the input itself,
 * which prevents iOS auto-zoom on focus. The 40px height accommodates the
 * larger text because vertical centering is automatic.
 *
 * Pass children rather than relying on a generic <input> inside, so callers
 * can use selects, textareas, or composed inputs.
 */
export default function FormField({ label, hint, error, required, children, className }) {
  return (
    <label className={clsx('block', className)}>
      <span className="block text-small font-medium text-gray-700 mb-1.5 select-none">
        {label}
        {required && (
          <span className="text-danger ml-0.5" aria-hidden="true">*</span>
        )}
      </span>
      {children}
      {error ? (
        <p role="alert" className="mt-1 text-caption text-danger">
          {error}
        </p>
      ) : hint ? (
        <p className="mt-1 text-caption text-gray-500">{hint}</p>
      ) : null}
    </label>
  );
}

/**
 * Convenience: a styled <input> matching FormField defaults. Use directly or
 * wrap in FormField for label/error.
 *
 * Visual parity with desktop:
 *   - `h-10` (40px) — same as desktop drawer-form / Input.jsx
 *   - `px-3` — same horizontal padding
 *   - `rounded-md` — same border radius
 *   - `border-gray-300 hover:border-gray-400` — same border treatment
 *   - `ring-2 ring-blue-600 ring-offset-0` on focus — same ring
 *
 * Mobile divergence:
 *   - `text-body` (16px) instead of desktop's `text-sm` (14px). Prevents iOS
 *     from auto-zooming when the input is focused. Does not break layout
 *     because at h-10 (40px), 16px text + line-height-24px centers cleanly.
 */
export function TextInput({ className, error, ...props }) {
  return (
    <input
      {...props}
      aria-invalid={error ? true : undefined}
      className={clsx(
        'block w-full h-10 px-3 text-body text-gray-900 placeholder-gray-400',
        'rounded-md border transition-colors duration-150',
        'focus:outline-none focus:ring-2 focus:ring-offset-0',
        error
          ? 'border-red-400 bg-red-50 focus:ring-red-500'
          : 'border-gray-300 bg-white hover:border-gray-400 focus:ring-amber-500 focus:border-amber-500',
        props.disabled ? 'opacity-50 cursor-not-allowed bg-gray-50' : '',
        className,
      )}
    />
  );
}
